import React from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useLocation, Navigate } from "react-router-dom";
import { Container, Row, Col, Button, Alert, Spinner } from "react-bootstrap";
import { apiTokenRequest } from "./msalConfig";

function normalizeRoles(rawRoles) {
  if (!rawRoles) return [];
  if (Array.isArray(rawRoles)) return rawRoles;
  return [rawRoles];
}

function decodeJwtPayload(token) {
  if (!token) return {};
  const parts = token.split(".");
  if (parts.length < 2) return {};

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
}

export default function RequireAuth({
  children,
  requiredRoles = [],
  unauthorizedMessage = "Brak wymaganej roli. Skontaktuj się z administratorem.",
}) {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress, accounts } = useMsal();
  const location = useLocation();
  const [assignmentStatus, setAssignmentStatus] = React.useState("idle");
  const [accessTokenRoles, setAccessTokenRoles] = React.useState([]);
  const [authMessage, setAuthMessage] = React.useState("");

  const activeAccount = instance.getActiveAccount() || accounts?.[0];
  const accountKey = activeAccount?.homeAccountId ?? "";

  const accountRoles = React.useMemo(() => {
    const claims = activeAccount?.idTokenClaims || {};
    return normalizeRoles(claims.roles || claims.role || claims.groups);
  }, [accountKey]);

  const effectiveRoles =
    accessTokenRoles.length > 0 ? accessTokenRoles : accountRoles;

  const isAssigned = assignmentStatus === "assigned";
  const isCheckingAssignment = assignmentStatus === "checking" || assignmentStatus === "idle";

  const isAuthorized =
    requiredRoles.length === 0 ||
    requiredRoles.some((role) => effectiveRoles.includes(role));

  React.useEffect(() => {
    if (!isAuthenticated && !accountKey && inProgress === InteractionStatus.None) {
      setAuthMessage("Wybierz konto Microsoft, aby kontynuować.");
    }
  }, [isAuthenticated, inProgress, accountKey]);

  // Acquire access token once to check assignment + extract roles.
  const tokenFetchedRef = React.useRef("");

  React.useEffect(() => {
    if (!isAuthenticated || !activeAccount || inProgress !== InteractionStatus.None) {
      return;
    }
    // Already fetched for this account.
    if (tokenFetchedRef.current === accountKey) return;

    let isMounted = true;
    setAssignmentStatus("checking");

    instance
      .acquireTokenSilent({ ...apiTokenRequest, account: activeAccount })
      .then((resp) => {
        if (!isMounted) return;
        tokenFetchedRef.current = accountKey;
        const payload = decodeJwtPayload(resp.accessToken);
        const roles = normalizeRoles(payload.roles || payload.role || payload.groups);
        setAccessTokenRoles(roles);
        setAssignmentStatus("assigned");
      })
      .catch(() => {
        if (!isMounted) return;
        setAccessTokenRoles([]);
        setAssignmentStatus("not-assigned");
      });

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, accountKey, inProgress]);

  const handleRelogin = () => {
    if (inProgress !== InteractionStatus.None) return;

    instance.loginRedirect({
      scopes: ["openid", "profile", "email"],
      prompt: "select_account",
      state: location?.pathname || "/",
    });
  };

  if (!isAuthenticated) {
    const isBusy = inProgress !== InteractionStatus.None;
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={7} xl={6} className="text-center">
            <h4>Wymagane logowanie</h4>
            {authMessage && <Alert variant="warning">{authMessage}</Alert>}
            <Button variant="primary" onClick={handleRelogin} disabled={isBusy}>
              {isBusy ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Logowanie...
                </>
              ) : (
                "Zaloguj się kontem Microsoft"
              )}
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  if (isCheckingAssignment) {
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={7} xl={6} className="text-center">
            <Spinner animation="border" size="sm" />
            <span className="ms-2">Sprawdzanie dostępu...</span>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!isAssigned) {
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={7} xl={6} className="text-center">
            <Alert variant="danger">Brak przypisania do aplikacji w Azure AD.</Alert>
            <Button variant="outline-primary" onClick={handleRelogin}>
              Wybierz inne konto
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!isAuthorized) {
    return <Navigate to="/no-access" replace />;
  }

  return children;
}